import { parseRoute, routeHref } from './navigation';

/**
 * Same production Clerk instance and hosted account pages as Planner. profile.labs.240.org
 * shares the primary 240.org domain. Development uses its configured test instance. Sign-in
 * returns to the route it started on, so a visitor signing in on `/p/<slug>` lands back there.
 */
export const HOSTED_SIGN_IN_HOST = 'profile.labs.240.org';
export function clerkConfiguration(origin: string, pathname = '/') {
  const url = new URL(origin);
  const destination = `${url.origin}${routeHref(parseRoute(pathname))}`;
  const hosted = url.hostname === HOSTED_SIGN_IN_HOST;
  return {
    hosted,
    provider: {
      ...(hosted ? {
        signInUrl: 'https://accounts.240.org/sign-in',
        signUpUrl: 'https://accounts.240.org/sign-up',
        allowedRedirectDomains: ['240.org', '240tutoring.com', 'study.240tutoring.com'],
      } : {}),
      signInForceRedirectUrl: destination,
      signUpForceRedirectUrl: destination,
      signInFallbackRedirectUrl: destination,
      signUpFallbackRedirectUrl: destination,
      afterSignOutUrl: `${url.origin}/`,
    },
  };
}

/**
 * Browser tests only (U8): which compiled-in `VITE_E2E_IDENTITY` entry this tab is, held in
 * `sessionStorage` so one browser context is one viewer. `E2E_SIGNED_OUT` is the visitor with
 * no sign-in. Nothing here is read when a Clerk key is configured.
 */
export const E2E_IDENTITY_KEY = 'profile.e2e-identity';
export const E2E_SIGNED_OUT = 'signed-out';

/**
 * Pure: the entry this tab is signed in as, or null for signed out. No choice means the first
 * entry, so a build with one identity lands inside the app; a stored choice must name a
 * compiled-in entry (whole or by user id), so a stale one cannot become an unknown token.
 */
export function selectE2eIdentity(raw: string | undefined, chosen: string | null): string | null {
  const identities = (raw ?? '').split(',').map((entry) => entry.trim()).filter(Boolean);
  if (chosen === null) return identities[0] ?? null;
  return identities.find((entry) => entry === chosen || entry.split(':')[0] === chosen) ?? null;
}

/** The one impure read; null whenever storage is unavailable. */
export function chosenE2eIdentity(): string | null {
  try { return window.sessionStorage.getItem(E2E_IDENTITY_KEY); } catch { return null; }
}
